'use client'

import { useMutation } from '@tanstack/react-query'
import { Bell, ChevronDown, CircleUser, HelpCircle, LogOut } from 'lucide-react'
import Link from 'next/link'
import { useCallback, useEffect, useState } from 'react'

import { ToolsIcon } from '@/components/icons'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/components/ui/avatar/index'
import { useWebSocket } from '@/hooks/useWebSocket'
import { cn } from '@/lib/cn'
import { authService } from '@/services/http/authService'
import {
  listenTotalUnreadNotifications,
  offListenTotalUnreadNotifications,
} from '@/services/socket'
import { emitTotalUnreadNotifications } from '@/services/socket/emitTotalUnreadNotifications'
import { cookiesActions } from '@/utils/cookies'
import { getInitials } from '@/utils/getInitials'
import { getUserRoleLabel } from '@/utils/getUserRoleLabel'
import { generateRoutePath } from '@/utils/routes/generateRoutePath'

import { NotificationPopoverContent } from '../notificationPopoverContent'

export interface IHeaderProps {
  className?: string
  user: {
    id?: string
    name?: string
    role?: string
    isAdmin?: boolean
    isManager?: boolean
    image?: string
    email?: string
  }
}

export function Header({ className, user }: IHeaderProps) {
  const { socket } = useWebSocket('/events-socket')

  const [unreadNotifications, setUnreadNotifications] = useState(0)

  const handleChangeUnreadNotifications = useCallback((unread: number) => {
    setUnreadNotifications(unread)
  }, [])

  const handleGetTotalUnreadNotifications = useCallback(() => {
    if (socket) {
      emitTotalUnreadNotifications(socket)
    }
  }, [socket])

  useEffect(() => {
    handleGetTotalUnreadNotifications()
  }, [handleGetTotalUnreadNotifications])

  // biome-ignore lint/correctness/useExhaustiveDependencies: revisar deps em tarefa própria
  useEffect(() => {
    if (socket) {
      listenTotalUnreadNotifications(socket, handleChangeUnreadNotifications)
    }

    return () => {
      if (socket) offListenTotalUnreadNotifications(socket)
    }
  }, [
    handleChangeUnreadNotifications,
    handleGetTotalUnreadNotifications,
    socket,
  ])

  useEffect(() => {
    document.title =
      unreadNotifications > 0 ? `Avantti (${unreadNotifications})` : 'Avantti'
  }, [unreadNotifications])

  const { mutateAsync } = useMutation({
    mutationFn: authService.endAccess,
    mutationKey: ['endAccess'],
  })

  const handleLogout = useCallback(() => {
    cookiesActions.deleteCookies('auth')
    cookiesActions.deleteCookies('user-info')
    window.location.href = '/login'

    // Logout happens in background, don't block redirect
    mutateAsync().catch((error) => {
      console.error('Error during logout:', error)
    })
  }, [mutateAsync])

  const userRoleLabel = getUserRoleLabel({
    isAdmin: user?.isAdmin,
    isManager: user?.isManager,
    role: user?.role,
  })

  return (
    <header
      className={cn(
        'flex justify-end items-center h-20 w-full px-6 py-0',
        className,
      )}
    >
      <div className="flex items-center gap-4">
        <HelpCircle className="size-6 gap-16 text-system-text-secondary" />

        <Popover>
          <PopoverTrigger asChild>
            <div className="relative flex cursor-pointer items-center">
              {unreadNotifications > 0 && (
                <div className="absolute -right-3 -top-3 flex size-5 items-center justify-center rounded-full bg-system-state-error">
                  <span className="text-[10px] font-semibold text-white">
                    {unreadNotifications}
                  </span>
                </div>
              )}
              <Bell className="size-6 text-system-text-secondary" />
            </div>
          </PopoverTrigger>
          <PopoverContent className="min-w-96 p-0 shadow-md">
            <NotificationPopoverContent />
          </PopoverContent>
        </Popover>

        <Separator
          className="h-10 bg-system-border-secondary"
          orientation="vertical"
        />

        <div className="flex items-center gap-2">
          <Avatar>
            {user.image && <AvatarImage src={user.image} />}
            {user.name && (
              <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
            )}
          </Avatar>
          <div className="flex flex-col">
            <span className="font-inter text-sm font-medium text-system-text-primary">
              {user?.name || ''}
            </span>
            <span className="font-inter text-xs font-medium text-system-text-secondary">
              {userRoleLabel}
            </span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger>
              <ChevronDown className="text-system-primary-icon" />
            </DropdownMenuTrigger>

            <DropdownMenuContent className="mr-2 p-4">
              <DropdownMenuGroup>
                <div className="flex w-40 flex-col items-center justify-center gap-2">
                  <Avatar className="size-24">
                    {user.image && <AvatarImage src={user.image} />}
                    {user.name && (
                      <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
                    )}
                  </Avatar>
                  <h2 className="font-inter text-sm font-semibold text-system-text-primary">
                    {user?.name || ''}
                  </h2>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="w-full cursor-default truncate text-center font-inter text-xs font-medium text-system-text-secondary">
                          {user?.email || ''}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{user?.email || ''}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <span className="font-inter text-xs font-medium text-system-text-secondary">
                    {userRoleLabel}
                  </span>
                </div>
              </DropdownMenuGroup>
              <DropdownMenuSeparator className="mt-2" />
              <DropdownMenuGroup>
                <DropdownMenuItem asChild>
                  <Link
                    href={generateRoutePath('profile')}
                    className="flex items-center gap-2"
                  >
                    <CircleUser />
                    Perfil
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild disabled>
                  <Link href={'/'} className="flex items-center gap-2">
                    <ToolsIcon />
                    Configurações
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator className="mt-2" />
              <DropdownMenuGroup>
                <DropdownMenuItem asChild>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2"
                  >
                    <LogOut />
                    Sair
                  </button>
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  )
}
